import React, { useState } from 'react';
import { Target, Play, Zap, Flag, FolderOpen, Home, StickyNote, Check, Lightbulb } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { STATUS_META, fmtDate, setLessonStatus } from './planningApi';
import { chapterColor } from './planningColors';
import { lessonParts, strategiesExtra } from './lessonLabels';

const PART_STYLE = {
  objective: { icon: Target, accent: 'border-l-sky-500' }, starter: { icon: Play, accent: 'border-l-amber-500' },
  main: { icon: Zap, accent: 'border-l-emerald-500', wide: true }, practise: { icon: Zap, accent: 'border-l-teal-500', wide: true },
  plenary: { icon: Flag, accent: 'border-l-violet-500' }, homework: { icon: Home, accent: 'border-l-rose-400' },
  resources: { icon: FolderOpen, accent: 'border-l-slate-400', wide: true },
};

const Part = ({ icon: Icon, label, value, accent }) => value ? (
  <div className={`rounded-lg border-l-4 ${accent} bg-slate-50 px-3 py-2`}>
    <p className="text-[11px] uppercase tracking-wide text-slate-400 flex items-center gap-1"><Icon className="h-3 w-3" />{label}</p>
    <p className="text-sm text-slate-700 whitespace-pre-line">{value}</p>
  </div>
) : null;

export const LessonCard = ({ lesson, canTick, isToday, onChange, isPrimary = false }) => {
  const [note, setNote] = useState(lesson.note || '');
  const [noteOpen, setNoteOpen] = useState(!!lesson.note);
  const [saving, setSaving] = useState(false);
  const meta = STATUS_META[lesson.status] || STATUS_META.not_yet;
  const col = chapterColor(lesson.chapter);
  const extra = strategiesExtra(lesson, isPrimary);

  const update = async (status, newNote = note) => {
    setSaving(true);
    try {
      await setLessonStatus(lesson.id, status, newNote || null);
      onChange(lesson.id, status, newNote || null);
      if (status === 'taught') toast.success(`Lesson ${lesson.lesson_no} marked as taught`);
    } catch { toast.error('Could not save'); } finally { setSaving(false); }
  };

  return (
    <div className={`rounded-xl border border-l-4 ${col.border} bg-white shadow-sm ${isToday ? 'ring-2 ring-emerald-400' : ''} ${lesson.status === 'taught' ? 'bg-emerald-50/30' : ''}`} data-testid={`lesson-card-${lesson.lesson_no}`}>
      <div className="flex flex-wrap items-start gap-3 px-4 py-3 border-b">
        <span className={`h-9 w-9 rounded-full flex items-center justify-center font-bold text-sm shrink-0 text-white ${lesson.status === 'taught' ? 'bg-emerald-600' : `bg-gradient-to-br ${col.bar}`}`}>
          {lesson.status === 'taught' ? <Check className="h-5 w-5" /> : lesson.lesson_no}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-slate-400">{lesson.term} · Week {lesson.week} · {lesson.day} {fmtDate(lesson.lesson_date)}{isToday && <Badge className="ml-2 bg-emerald-600 text-[10px]">Today</Badge>}</p>
          <p className="font-semibold text-slate-900">{lesson.topic}</p>
          <p className={`text-xs ${col.text}`}>{lesson.chapter}{lesson.sections ? ` · ${isPrimary ? 'Indicators' : 'Textbook'} ${lesson.sections}` : ''}{lesson.lesson_type && lesson.lesson_type !== 'Lesson' ? ` · ${lesson.lesson_type}` : ''}</p>
        </div>
        {canTick && (
          <div className="flex gap-1 flex-wrap" data-testid={`lesson-status-${lesson.lesson_no}`}>
            {Object.entries(STATUS_META).map(([key, m]) => (
              <button key={key} type="button" disabled={saving} onClick={() => update(key)} data-testid={`lesson-${lesson.lesson_no}-set-${key}`}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${lesson.status === key ? m.active : m.className}`}>{m.label}</button>
            ))}
          </div>
        )}
        {!canTick && <Badge variant="outline" className={meta.className}>{meta.label}</Badge>}
      </div>
      <div className="p-4 grid gap-2 md:grid-cols-2">
        {lessonParts(lesson, isPrimary).map((p) => {
          const st = PART_STYLE[p.key];
          return (
            <div key={p.key} className={st.wide ? 'md:col-span-2' : ''}>
              <Part icon={st.icon} label={p.label} value={p.value} accent={st.accent} />
            </div>
          );
        })}
        {Object.keys(extra).length > 0 && (
          <div className="md:col-span-2 rounded-lg border border-cyan-200 bg-cyan-50/50 p-3 space-y-2" data-testid={`lesson-strategies-${lesson.lesson_no}`}>
            <p className="text-[11px] uppercase tracking-wide text-cyan-700 flex items-center gap-1 font-semibold"><Lightbulb className="h-3 w-3" />Teaching strategies for this lesson</p>
            <div className="grid gap-2 md:grid-cols-2">
              {Object.entries(extra).map(([k, v]) => (
                <div key={k} className="text-xs"><span className="font-semibold text-slate-700">{k}: </span><span className="text-slate-600 whitespace-pre-line">{v}</span></div>
              ))}
            </div>
          </div>
        )}
        {canTick && (
          <div className="md:col-span-2">
            {noteOpen ? (
              <div className="flex gap-2 items-start">
                <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Teacher notes — what worked, what to revisit…" className="text-sm" data-testid={`lesson-note-${lesson.lesson_no}`} />
                <Button size="sm" variant="outline" disabled={saving} onClick={() => update(lesson.status === 'not_yet' && note ? 'not_yet' : lesson.status, note)} data-testid={`lesson-note-save-${lesson.lesson_no}`}>Save</Button>
              </div>
            ) : (
              <button type="button" className="text-xs text-slate-500 hover:text-emerald-700 flex items-center gap-1" onClick={() => setNoteOpen(true)}><StickyNote className="h-3 w-3" />Add teacher note</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
